import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Live shared form fields for the workgroup: local edits are sent after a short pause,
// everyone polls for changes. A field you typed in recently is never overwritten by the server.
const Ctx = createContext(null);
const POLL_MS = 1500;
const FLUSH_MS = 400;
const LOCAL_HOLD_MS = 2500;

export function SharedDraftProvider({ scope, children }) {
  const me = useAuth()?.user;
  const [fields, setFields] = useState({});
  const [viewers, setViewers] = useState([]);
  const ref = useRef({});
  const pending = useRef({});
  const touched = useRef({});
  const version = useRef(-1);
  const offset = useRef(0);  // server clock minus local clock
  const timer = useRef(null);

  const apply = (next) => { ref.current = next; setFields(next); };

  const flush = useCallback(async () => {
    const patch = pending.current;
    pending.current = {};
    if (!Object.keys(patch).length) return;
    try {
      await api.patchDraft(scope, patch);
    } catch (e) {
      pending.current = { ...patch, ...pending.current };
      console.warn("draft save failed", e);
    }
  }, [scope]);

  const set = useCallback((key, value) => {
    touched.current[key] = Date.now();
    pending.current[key] = value;
    apply({ ...ref.current, [key]: { value, by: { id: me?.id, name: me?.name }, at: new Date().toISOString() } });
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, FLUSH_MS);
  }, [flush, me]);

  useEffect(() => {
    let stop = false;
    let t;
    const tick = async () => {
      if (!document.hidden) {
        try {
          const r = await api.getDraft(scope, version.current);
          setViewers(r.viewers || []);
          if (r.server_now) offset.current = new Date(r.server_now).getTime() - Date.now();
          if (!r.unchanged) {
            version.current = r.version;
            const now = Date.now();
            const held = (k) => k in pending.current || now - (touched.current[k] || 0) < LOCAL_HOLD_MS;
            const next = {};
            for (const [k, f] of Object.entries(ref.current)) if (held(k)) next[k] = f;
            for (const [k, f] of Object.entries(r.fields || {})) if (!held(k)) next[k] = f;
            apply(next);
          }
        } catch (e) {
          console.warn("draft poll failed", e);
        }
      }
      if (!stop) t = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { stop = true; clearTimeout(t); flush(); };
  }, [scope, flush]);

  const clear = useCallback(async (prefix = "") => {
    for (const k of Object.keys(ref.current)) {
      if (k.startsWith(prefix)) { delete pending.current[k]; delete touched.current[k]; }
    }
    apply(Object.fromEntries(Object.entries(ref.current).filter(([k]) => !k.startsWith(prefix))));
    await api.clearDraft(scope, prefix);
  }, [scope]);

  const serverNow = useCallback(() => Date.now() + offset.current, []);
  return <Ctx.Provider value={{ fields, viewers, set, clear, me, serverNow }}>{children}</Ctx.Provider>;
}

/** Drop-in for useState whose value is shared live with the workgroup. Supports functional updates. */
export function useShared(key, initial) {
  const ctx = useContext(Ctx);
  const [init] = useState(initial);
  const f = ctx.fields[key];
  const value = f === undefined ? init : f.value;
  const latest = useRef(value);
  latest.current = value;
  const setter = useCallback((x) => {
    const next = typeof x === "function" ? x(latest.current) : x;
    latest.current = next;
    ctx.set(key, next);
  }, [ctx.set, key]);  // eslint-disable-line react-hooks/exhaustive-deps
  return [value, setter];
}

export const useSharedDraft = () => useContext(Ctx);

const TYPING_MS = 4000;

const ago = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
};

/** "Tess is typing…" while another member's edit is fresh, then "✎ Tess · 12s ago". */
export function TypingMark({ name, byId, at, server = false, className = "", testId }) {
  const ctx = useContext(Ctx);
  const [, tick] = useState(0);
  const mine = byId ? byId === ctx?.me?.id : name === ctx?.me?.name;
  const now = server ? ctx.serverNow() : Date.now();
  const age = at ? now - new Date(at).getTime() : Infinity;
  const typing = !mine && age < TYPING_MS;
  useEffect(() => {
    if (!typing) return undefined;
    const t = setTimeout(() => tick((x) => x + 1), TYPING_MS - age + 50);
    return () => clearTimeout(t);
  }, [typing, age]);
  if (!name) return null;
  if (typing) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] text-amber-300 ${className}`} data-testid={testId} data-typing="true">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> {name} is typing…
      </span>
    );
  }
  return (
    <span className={`text-[10px] ${mine ? "text-muted-foreground" : "text-amber-300"} ${className}`} data-testid={testId}>
      ✎ {mine ? "you" : name}{at && Number.isFinite(age) ? ` · ${ago(age)}` : ""}
    </span>
  );
}

/** "written by X" marker for a shared field (shows a typing hint while someone else edits it). */
export function EditedBy({ field, className = "" }) {
  const { fields } = useContext(Ctx);
  const f = fields[field];
  const empty = !f || f.value === "" || f.value === null || (Array.isArray(f.value) && !f.value.length);
  if (empty || !f.by?.name) return null;
  return <TypingMark name={f.by.name} byId={f.by.id} at={f.at} server className={className} testId={`edited-by-${field}`} />;
}

/** Shows who started a shared run (analysis / drafting) when it's someone else. */
export function RunningBy({ field, label = "Analysis running" }) {
  const { fields, me } = useContext(Ctx);
  const f = fields[field];
  if (!f?.value || !f.by?.name || f.by.id === me?.id) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-amber-300" data-testid={`running-by-${field}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> {label} — started by {f.by.name}
    </span>
  );
}

const WATCH_MS = 5000;
const GRACE_MS = 8000;

/**
 * Watches a shared run started by someone else. If they leave mid-run the job is
 * finished here from the server (onComplete) or the stuck "running" state is cleared (onStop).
 */
export function useRunWatcher({ loading, localRunning, kind, jobId, noJobTimeoutMs = 30000, onComplete, onStop }) {
  const cb = useRef({ onComplete, onStop });
  cb.current = { onComplete, onStop };
  useEffect(() => {
    if (!loading || localRunning) return undefined;
    const started = Date.now();
    let endedAt = null;
    const check = async () => {
      if (!jobId) {
        if (Date.now() - started > noJobTimeoutMs) cb.current.onStop("The run was interrupted — run it again.");
        return;
      }
      try {
        const job = await api.jobStatus(kind, jobId);
        if (!["complete", "error", "cancelled"].includes(job.status)) return;
        endedAt = endedAt || Date.now();
        if (Date.now() - endedAt < GRACE_MS) return;  // give the person who started it time to publish
        if (job.status === "complete") cb.current.onComplete(job);
        else cb.current.onStop(job.status === "cancelled" ? "Analysis cancelled." : job.error || "Analysis failed");
      } catch (e) {
        if (e?.response?.status === 404) cb.current.onStop("The run was interrupted (the job is gone) — run it again.");
      }
    };
    const t = setInterval(check, WATCH_MS);
    check();
    return () => clearInterval(t);
  }, [loading, localRunning, kind, jobId, noJobTimeoutMs]);
}
